import { useEffect } from 'react';

import { conversationSteps } from '../utils/conversationFlow';
import { askAIQuestion, getInterestCategories } from '../utils/api';
import { saveConversationToStorage } from '../utils/storage';
import { replacePlaceholders, nextMessageId } from '../utils/aiResponseProcessor';
import { buildMessageHistory } from '../utils/helpers';

const useConversationInit = ({
  state, dispatch, isOpen, setScrollPosition, config,
}) => {
  const { communityNid } = config || {};

  const initializeConversation = () => {
    dispatch({ type: 'SET_HAS_SHOWN_FOLLOW_UP', payload: false });
    setScrollPosition(0);

    const currentStep = conversationSteps[state.conversationState.currentStep];

    const buttons = currentStep.buttons || [];

    const initialMessage = {
      id: nextMessageId(),
      type: 'bot',
      text: replacePlaceholders(currentStep.message, state.conversationState, config),
      instructions: currentStep.instructions || null,
      buttons,
      continueButton: currentStep.continueButton || null,
      formFields: currentStep.formFields || null,
      submitButton: currentStep.submitButton || null,
      clearDataLink: currentStep.clearDataLink || null,
      stepType: currentStep.type,
    };

    dispatch({ type: 'SET_MESSAGES', payload: [initialMessage] });
    saveConversationToStorage(
      state.sessionId,
      [initialMessage],
      state.conversationState,
      {},
      state.amenityPlaces,
    );
  };

  const handleAIQuestion = async (question, isClosureResponse = false) => {
    const context = {
      sessionId: state.sessionId,
      selectedInterests: state.conversationState.selectedInterests.map((i) => i.uuid),
      userInfo: state.conversationState.userInfo,
      additionalInterests: state.conversationState.additionalInterests,
      communityNid,
      communityName: config?.communityName,
      currentStep: state.conversationState.currentStep,
      isClosureResponse,
      shownPlaceUuids: state.amenityPlaces.map((p) => p.uuid).filter(Boolean),
      messageHistory: buildMessageHistory(state.messages),
    };

    try {
      dispatch({ type: 'SET_IS_AI_THINKING', payload: { active: true, withLabel: true } });
      const aiResponse = await askAIQuestion(question, context);

      dispatch({
        type: 'APPEND_MESSAGES',
        payload: [{
          id: nextMessageId() + 1,
          type: 'bot',
          text: aiResponse.response,
          stepType: 'ai_response',
        }],
      });
      dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
    } catch (error) {
      console.error('[TourGuideAi] Error in handleAIQuestion:', error);
      dispatch({ type: 'SET_IS_AI_THINKING', payload: false });
      dispatch({
        type: 'APPEND_MESSAGES',
        payload: [{
          id: nextMessageId() + 1,
          type: 'bot',
          text: 'I apologize, but I\'m having trouble processing your question right now. Please try again later.',
          stepType: 'error',
        }],
      });
    }
  };

  const fetchInterestCategories = async () => {
    dispatch({ type: 'SET_INTEREST_CATEGORIES_LOADING', payload: true });
    const data = await getInterestCategories();
    dispatch({ type: 'SET_INTEREST_CATEGORIES', payload: data.categories || [] });
  };

  // Initialize conversation (first message) when panel opens with no messages
  useEffect(() => {
    if (isOpen && state.messages.length === 0) {
      initializeConversation();
      fetchInterestCategories();
    }
  }, [isOpen]);

  return { initializeConversation, handleAIQuestion };
};

export default useConversationInit;
